import React, { useRef, useState, useEffect, useMemo } from 'react'
import PageConfig from '@/component/layout/PageConfig.tsx'
import ChangeSettingsModal from '@/pages/settings/ChangeSettingsModal.tsx'
import DeleteAccountModal from '@/pages/settings/DeleteAccountModal.tsx'
import { useTranslation } from 'react-i18next'
import { useLoginStore } from '@/store/LoginStore.ts'
import { toast } from 'react-toastify'
import useQueryGetSettings, { useMutationUpdateSettings } from '@/api/settings/settings.ts';
import { useQueryClient, useMutation, useQuery } from '@tanstack/react-query'
import { QueryCacheKeys } from '@/api/queryKeys.ts'
import { apiGetCurrentSetting } from '@/api/auth/getCurrentSetting.ts'
import { apiPatchDeviceSetting } from '@/api/auth/patchDeviceSetting.ts'
import Select, { ISelectOption } from '@/component/input/Select.tsx'
import useQueryGetMe from '@/api/auth/getMe.ts'

const Settings: React.FC = () => {
    const changePasswordModalRef = useRef<any>()
    const changeSettingsModalRef = useRef<any>()
    const changeSafeZoneModalRef = useRef<any>()
    const changeContactModalRef = useRef<any>()
    const deleteAccountModalRef = useRef<any>()
    const { t } = useTranslation()
    const { logout } = useLoginStore()
    const queryClient = useQueryClient()

    /** 사용자 설정 데이터 가져오기 */
    const { data, isLoading, refetch } = useQueryGetSettings()
    const { mutate: updateSettings } = useMutationUpdateSettings();
    const { data: me } = useQueryGetMe()

    /** 상태 값 설정 */
    const [safeZoneRadius, setSafeZoneRadius] = useState<number>(100)
    const [isPushEnabled, setIsPushEnabled] = useState<boolean>(true)
    const [deviceSerial, setDeviceSerial] = useState<string>('')

    // 현재 설정 조회 (센싱주기용)
    const { data: currentSetting, refetch: refetchSetting, isLoading: isCurrentSettingLoading } = useQuery({
        queryKey: ['currentSetting'],
        queryFn: apiGetCurrentSetting,
        enabled: !!me, // 사용자 정보가 있을 때만 실행
    })
    
    // 센싱주기 설정 상태
    const [sensingInterval, setSensingInterval] = useState<ISelectOption>()
    
    // 센싱주기 옵션 목록 (2분부터 1분 단위로 60분까지)
    const sensingIntervalOptions: ISelectOption[] = useMemo(() => {
        const options: ISelectOption[] = []
        for (let i = 2; i <= 60; i++) {
            options.push({
                id: i.toString(),
                text: `${i}분`,
                value: i
            })
        }
        return options
    }, [])
    
    // 디바이스 설정 업데이트 mutation
    const deviceSettingMutation = useMutation({
        mutationFn: apiPatchDeviceSetting,
        onSuccess: (data) => {
            if (Number(data.code) === 200) {
                // 설정 새로고침
                refetchSetting()
                toast.success('센싱주기가 성공적으로 설정되었습니다.')
                // 성공 시 3초 후 상태 리셋
                setTimeout(() => {
                    deviceSettingMutation.reset()
                }, 3000)
            } else {
                // 서버 에러로 처리
                throw new Error(`서버 에러: ${data.message}`)
            }
        },
        onError: (_error: any) => {
            toast.error('센싱주기 설정에 실패했습니다. 다시 시도해주세요.')
            // 에러 시 5초 후 상태 리셋  
            setTimeout(() => {
                deviceSettingMutation.reset()
            }, 5000)
        }
    })

    // 센싱주기 변경 핸들러
    const handleSensingIntervalChange = (option: ISelectOption) => {
        setSensingInterval(option)
        
        // 현재 설정 로딩 중이면 처리하지 않음
        if (isCurrentSettingLoading) {
            return
        }
        
        // 현재 설정값을 기반으로 센싱주기만 변경해서 API 호출
        if (currentSetting?.data) {
            deviceSettingMutation.mutate({
                safeZoneRadiusM: currentSetting.data.safeZoneRadiusM,
                isAppPush: currentSetting.data.isAppPush,
                contact: currentSetting.data.contact,
                address: currentSetting.data.address,
                latitude: currentSetting.data.latitude,
                longitude: currentSetting.data.longitude,
                dataIntervalMinute: option.value
            })
        } else {
            // 현재 설정이 없으면 기본값과 함께 전송
            deviceSettingMutation.mutate({
                safeZoneRadiusM: 250, // 기본 안전구역 반경 250m
                isAppPush: true, // 기본 푸시 알림 활성화
                contact: '', // 빈 연락처
                address: '', // 빈 주소
                latitude: 37.5665, // 서울 위도 기본값
                longitude: 126.9780, // 서울 경도 기본값
                dataIntervalMinute: option.value
            })
        }
    }

    // 모달 컨트롤 함수 - 모달 열기 전에 데이터 최신화
    const openDeviceSerialModal = () => {
        // 최신 데이터로 refetch하여 최신 상태 반영
        refetch().then(() => {
            changeSettingsModalRef.current?.show()
        })
    }

    /** API 데이터를 상태에 반영 */
    useEffect(() => {
        if (data) {
            setSafeZoneRadius(data.safeZoneRadiusM)
            setIsPushEnabled(data.isAppPush)
            setDeviceSerial(data.deviceSerial ?? t('settings.device.noInfo'))
        }
    }, [data])

    // 초기 센싱주기 설정 (서버에서 가져온 값 또는 기본값: 10분)
    useEffect(() => {
        // 현재 설정이 로딩 중이면 대기
        if (isCurrentSettingLoading) {
            return
        }

        // 옵션이 준비되지 않았으면 대기
        if (sensingIntervalOptions.length === 0) {
            return
        }

        // 이미 설정되어 있고, 서버 값과 일치하면 중복 설정 방지
        if (sensingInterval && currentSetting?.data?.dataIntervalMinute === sensingInterval.value) {
            return
        }

        if (currentSetting?.data?.dataIntervalMinute !== undefined && currentSetting.data.dataIntervalMinute !== null) {
            // 서버에서 가져온 현재 설정값으로 초기화
            const serverValue = Number(currentSetting.data.dataIntervalMinute)
            
            // 유효한 숫자인지 확인
            if (isNaN(serverValue) || serverValue <= 0) {
                const defaultOption = sensingIntervalOptions.find(option => option.value === 10)
                setSensingInterval(defaultOption || sensingIntervalOptions[8])
                return
            }
            
            const currentOption = sensingIntervalOptions.find(option => option.value === serverValue)
            if (currentOption) {
                setSensingInterval(currentOption)
            } else {
                // 서버 값이 옵션에 없으면 가장 가까운 값으로 설정하거나 기본값 사용
                if (serverValue >= 2 && serverValue <= 60) {
                    // 서버 값을 옵션에 임시로 추가
                    const tempOption = { id: serverValue.toString(), text: `${serverValue}분`, value: serverValue }
                    setSensingInterval(tempOption)
                } else {
                    // 범위를 벗어나면 기본값 사용
                    const defaultOption = sensingIntervalOptions.find(option => option.value === 10)
                    setSensingInterval(defaultOption || sensingIntervalOptions[8])
                }
            }
        } else {
            // 서버 설정이 없으면 기본값 (10분) 설정
            const defaultOption = sensingIntervalOptions.find(option => option.value === 10)
            setSensingInterval(defaultOption || sensingIntervalOptions[8])
        }
    }, [currentSetting, sensingIntervalOptions, sensingInterval, isCurrentSettingLoading])

    const userAddress = data?.address ?? t('settings.address.noInfo')
    const userEmail = data?.id ?? t('settings.email.noInfo')
    const userContact = data?.contact ?? t('settings.contact.noInfo')

    /** 푸시 알림 토글 */
    const togglePushNotification = async () => {
        const newPushState = !isPushEnabled
        setIsPushEnabled(newPushState)

        updateSettings(
            { isAppPush: newPushState },
            {
                onSuccess: () => {
                    toast.success(t(`settings.notification.${newPushState ? 'on' : 'off'}`))

                    queryClient.setQueryData(['settings'], (oldData: any) => ({
                        ...oldData,
                        isAppPush: newPushState,
                    }))

                    refetch()
                },
                onError: () => {
                    toast.error(t('settings.notification.error'))
                    setIsPushEnabled(!newPushState) // 롤백
                },
            }
        )
    }

    /** 안심구역 반경 변경 */
    const handleSafeZoneChange = (newRadius: number) => {
        setSafeZoneRadius(newRadius)

        // localStorage에 즉시 반영 (추가)
        localStorage.setItem("safeZoneRadius", newRadius.toString());

        updateSettings(
            { safeZoneRadiusM: newRadius },
            {
                onSuccess: () => {
                    toast.success(t('settings.safeZone.updated'))

                    queryClient.setQueryData(['settings'], (oldData: any) => ({
                        ...oldData,
                        safeZoneRadiusM: newRadius,
                    }))

                    // 안심구역 상태 쿼리 캐시 무효화 (추가)
                    queryClient.invalidateQueries({ queryKey: QueryCacheKeys.alerts.safeZoneStatus() });

                    refetch()
                },
                onError: () => {
                    toast.error(t('settings.safeZone.error'))
                    // 실패 시 localStorage 롤백 (추가)
                    localStorage.setItem("safeZoneRadius", safeZoneRadius.toString());
                },
            }
        )
    }

    /** 안심구역 주소 변경 */
    const handleAddressChange = (address: string, latitude: string, longitude: string) => {
        // 쿼리 데이터 업데이트
        queryClient.setQueryData(['settings'], (oldData: any) => ({
            ...oldData,
            address: address,
            latitude: parseFloat(latitude),
            longitude: parseFloat(longitude),
        }))

        toast.success(t('settings.safeZone.addressUpdated'))
        refetch()
    }

    /** 연락처 변경 */
    const handleContactChange = (key: string, newValue: string) => {
        if (key === 'contact') {
            queryClient.setQueryData(['settings'], (oldData: any) => ({
                ...oldData,
                contact: newValue,
            }))
            toast.success(t('settings.contact.updated'))
            refetch()
        }
    }

    /** 로그아웃 처리 */
    const handleLogout = async () => {
        await logout()
        clearCache()
        toast.success(t('topNav.menu.logout.success'))
        window.location.href = '/login'
    }

    /** 비밀번호 변경 후 자동 로그아웃 */
    const handlePasswordChange = async () => {
        await logout()
        clearCache()
        toast.success(t('account.passwordChange.success'))
        window.location.href = '/login'
    }

    /** 캐시 데이터 삭제 */
    const clearCache = () => {
        sessionStorage.clear()
        localStorage.clear()
    }

    /** 설정 변경 핸들러 (디바이스 시리얼 변경) */
    const handleSettingChange = (key: string, newValue: string) => {
        if (key === 'deviceSerial' && newValue) {
            // API 업데이트가 성공한 경우에만 UI 업데이트
            setDeviceSerial(newValue)
            toast.success(t('settings.device.serialUpdated'))
            
            // 쿼리 데이터 업데이트
            queryClient.setQueryData(['settings'], (oldData: any) => {
                if (!oldData) return { deviceSerial: newValue };
                return {
                    ...oldData,
                    deviceSerial: newValue,
                }
            })
            
            // 안심구역 상태와 같은 관련 쿼리 무효화 고려
            refetch()
        }
    }

    return (
        <>
            <PageConfig showTopNav={true} showFooter={true}>
                <div className="w-full h-full flex flex-col">
                    {/* 상단 제목 */}
                    <div className="px-4 py-3 border-b bg-white sticky top-0 z-10">
                        <h1 className="text-heading1 text-center">{t('settings.title')}</h1>
                    </div>

                    {/* 스크롤 가능한 내용 - 이중 스크롤 해결 */}
                    <div className="flex-1 px-4 mt-4 space-y-4 pb-48 md:pb-40">
                        {/* 디바이스 설정 */}
                        <div>
                            <h2 className="text-sm text-gray-500 mb-2">{t('settings.device.title')}</h2>
                            <div 
                                className="bg-white p-4 rounded-lg shadow border-b cursor-pointer"
                                onClick={openDeviceSerialModal}
                            >
                                <h3 className="text-base font-semibold">{t('settings.device.register')}</h3>
                                <p className="text-sm text-gray-500">{isLoading ? '로딩 중...' : deviceSerial}</p>
                            </div>
                        </div>

                        {/* 센싱주기 설정 */}
                        <div>
                            <h2 className="text-sm text-gray-500 mb-2">📡 {t('users.detail.setting.sensingInterval.title')}</h2>
                            <div className="bg-white p-4 rounded-lg shadow border-b">
                                <div className="space-y-4">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h3 className="text-base font-semibold">{t('users.detail.setting.sensingInterval.label')}</h3>
                                            <p className="text-sm text-gray-500">{t('users.detail.setting.sensingInterval.description.line1')}</p>
                                        </div>
                                        <div className="w-[120px]">
                                            <Select
                                                options={sensingIntervalOptions}
                                                value={sensingInterval}
                                                onChanged={handleSensingIntervalChange}
                                                disabled={deviceSettingMutation.isPending || isCurrentSettingLoading}
                                                className={''}
                                            />
                                        </div>
                                    </div>
                                    
                                    {/* API 상태 표시 */}
                                    {isCurrentSettingLoading && (
                                        <div className="bg-blue-50 p-3 rounded-lg border-l-4 border-blue-400">
                                            <p className="text-xs text-blue-700">
                                                ⏳ {t('users.detail.setting.sensingInterval.status.loading')}
                                            </p>
                                        </div>
                                    )}
                                    
                                    {deviceSettingMutation.isPending && (
                                        <div className="bg-yellow-50 p-3 rounded-lg border-l-4 border-yellow-400">
                                            <p className="text-xs text-yellow-700">
                                                ⏳ {t('users.detail.setting.sensingInterval.status.updating')}
                                            </p>
                                        </div>
                                    )}
                                    
                                    {deviceSettingMutation.isError && (
                                        <div className="bg-red-50 p-3 rounded-lg border-l-4 border-red-400">
                                            <p className="text-xs text-red-700">
                                                ❌ {t('users.detail.setting.sensingInterval.status.error')}
                                            </p>
                                        </div>
                                    )}
                                    
                                    {deviceSettingMutation.isSuccess && !deviceSettingMutation.isPending && (
                                        <div className="bg-green-50 p-3 rounded-lg border-l-4 border-green-400">
                                            <p className="text-xs text-green-700">
                                                ✅ 센싱주기가 성공적으로 설정되었습니다.
                                            </p>
                                        </div>
                                    )}
                                    
                                    {!isCurrentSettingLoading && !currentSetting && (
                                        <div className="bg-orange-50 p-3 rounded-lg border-l-4 border-orange-400">
                                            <p className="text-xs text-orange-700">
                                                ⚠️ {t('users.detail.setting.sensingInterval.status.loadError')}
                                            </p>
                                        </div>
                                    )}
                                    
                                    <div className="bg-blue-50 p-3 rounded-lg border border-blue-200">
                                        <p className="text-xs text-blue-700">
                                            ℹ️ {t('users.detail.setting.sensingInterval.description.line2')}
                                            {sensingInterval && (
                                                <>
                                                    <br />
                                                    <span className="font-medium">{t('users.detail.setting.sensingInterval.currentSetting.prefix')} {sensingInterval.text}</span>
                                                </>
                                            )}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* 안심구역 설정 */}
                        <div>
                            <h2 className="text-sm text-gray-500 mb-2">{t('settings.safeZone.title')}</h2>
                            <div 
                                className="bg-white p-4 rounded-lg shadow border-b cursor-pointer"
                                onClick={() => changeSafeZoneModalRef.current?.show()}
                            >
                                <h3 className="text-base font-semibold">{t('settings.safeZone.register')}</h3>
                                <p className="text-sm text-gray-500">{isLoading ? '로딩 중...' : userAddress}</p>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow border-b">
                                <h3 className="text-base font-semibold">{t('settings.safeZone.radius')}</h3>
                                <p className="text-sm text-gray-500">{safeZoneRadius}m</p>
                                <input
                                    type="range"
                                    min="10"
                                    max="1000"
                                    step="10"
                                    value={safeZoneRadius}
                                    onChange={(e) => handleSafeZoneChange(Number(e.target.value))}
                                    className="w-full"
                                />
                            </div>
                        </div>

                        {/* 알림 설정 */}
                        <div>
                            <h2 className="text-sm text-gray-500 mb-2">{t('settings.notification.title')}</h2>
                            <div className="bg-white p-4 rounded-lg shadow border-b flex justify-between items-center">
                                <div>
                                    <h3 className="text-base font-semibold">{t('settings.notification.push')}</h3>
                                    <p className="text-sm text-gray-500">
                                        {isPushEnabled ? t('settings.notification.on') : t('settings.notification.off')}
                                    </p>
                                </div>
                                <input
                                    type="checkbox"
                                    className="toggle toggle-primary"
                                    checked={isPushEnabled}
                                    onChange={togglePushNotification}
                                />
                            </div>
                        </div>

                        {/* 계정 설정 */}
                        <div>
                            <h2 className="text-sm text-gray-500 mb-2">{t('settings.user.title')}</h2>
                            <div className="bg-white p-4 rounded-lg shadow border-b">
                                <h3 className="text-base font-semibold">ID</h3>
                                <p className="text-sm text-gray-500">{isLoading ? '로딩 중...' : userEmail}</p>
                            </div>
                            <div 
                                className="bg-white p-4 rounded-lg shadow border-b cursor-pointer"
                                onClick={() => changeContactModalRef.current?.show()}
                            >
                                <h3 className="text-base font-semibold">{t('settings.user.contact')}</h3>
                                <p className="text-sm text-gray-500">{isLoading ? '로딩 중...' : userContact}</p>
                            </div>
                        </div>

                        {/* 계정 관리 */}
                        <div>
                            <div className="bg-white p-4 rounded-lg shadow border-b cursor-pointer"
                                 onClick={() => changePasswordModalRef.current?.show()}>
                                <h3 className="text-base font-semibold">{t('settings.user.passwordChange')}</h3>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow border-b cursor-pointer"
                                 onClick={handleLogout}>
                                <h3 className="text-base font-semibold text-red-500">{t('settings.user.logout')}</h3>
                            </div>
                            <div className="bg-white p-4 rounded-lg shadow border-b cursor-pointer"
                                 onClick={() => deleteAccountModalRef.current?.show()}>
                                <h3 className="text-base font-semibold text-red-600">{t('settings.user.delete')}</h3>
                            </div>
                        </div>
                    </div>
                </div>
            </PageConfig>

            {/* 비밀번호 변경 (password 모드) */}
            <ChangeSettingsModal
                ref={changePasswordModalRef}
                title={t('settings.user.passwordChange')}
                settingKey="password"
                type="password"
                placeholder={t('settings.password.newPlaceholder')}
                onPasswordChange={handlePasswordChange}
            />

            {/* 디바이스 시리얼 변경 (text 모드) */}
            <ChangeSettingsModal
                ref={changeSettingsModalRef}
                title={t('settings.device.changeSerial')}
                settingKey="deviceSerial"
                type="text"
                placeholder={deviceSerial}
                currentValue={deviceSerial}
                onSettingChange={handleSettingChange}
            />

            {/* 안심구역 주소 변경 (address 모드) */}
            <ChangeSettingsModal
                ref={changeSafeZoneModalRef}
                title={t('settings.safeZone.changeAddress')}
                settingKey="address"
                type="address"
                placeholder={t('settings.safeZone.addressPlaceholder')}
                currentAddress={userAddress}
                onAddressChange={handleAddressChange}
            />

            {/* 연락처 변경 (text 모드) */}
            <ChangeSettingsModal
                ref={changeContactModalRef}
                title={t('settings.contact.changeContact')}
                settingKey="contact"
                type="text"
                placeholder={t('settings.contact.placeholder')}
                currentValue={userContact}
                onSettingChange={handleContactChange}
            />

            {/* 회원탈퇴 모달 */}
            <DeleteAccountModal
                ref={deleteAccountModalRef}
            />
        </>
    )
}

export default Settings
